import { useRef } from "react";
import { useLandingSettings, isUsableUrl } from "@/context/LandingSettingsContext";

/**
 * Two-button CTA row with a very subtle magnetic hover on each button.
 * Buttons are real DOM elements, keyboard-accessible, semantic.
 *
 * If `mintHref` / `marketplaceHref` are not provided, the buttons fall
 * back to the URLs configured in the Landing Page Admin panel
 * (`settings.mint_url` and `settings.magiceden_url` → `settings.tensor_url`).
 * When no usable URL exists the button is rendered as an inert element
 * with a subtle disabled state (reduced opacity, default cursor) — it
 * will never navigate, reload, or open a new tab.
 */
function useMagnetic() {
  const ref = useRef(null);
  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - (rect.left + rect.width / 2);
    const relY = e.clientY - (rect.top + rect.height / 2);
    // Very subtle: max ~4px in each axis.
    el.style.transform = `translate(${relX * 0.08}px, ${relY * 0.12}px)`;
  };
  const onMouseLeave = () => {
    if (ref.current) ref.current.style.transform = "";
  };
  return { ref, onMouseMove, onMouseLeave };
}

const disabledStyle = { opacity: 0.45, cursor: "default" };

export function HeroButtons({ mintHref, marketplaceHref }) {
  const { settings } = useLandingSettings();
  const mint = useMagnetic();
  const market = useMagnetic();

  const resolvedMint   = mintHref        || settings.mint_url;
  const resolvedMarket = marketplaceHref || settings.magiceden_url || settings.tensor_url;
  const mintUsable   = isUsableUrl(resolvedMint);
  const marketUsable = isUsableUrl(resolvedMarket);

  return (
    <div
      data-testid="hero-cta"
      className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
    >
      {mintUsable ? (
        <a
          ref={mint.ref}
          onMouseMove={mint.onMouseMove}
          onMouseLeave={mint.onMouseLeave}
          href={resolvedMint}
          target="_blank"
          rel="noreferrer"
          data-testid="hero-mint"
          data-cursor="hover"
          className="prophets-mint-btn p-nav inline-flex items-center justify-center min-w-[172px] px-8 py-3.5 rounded-sm text-[11px] tracking-[0.4em] uppercase text-white"
        >
          Mint
        </a>
      ) : (
        <button
          ref={mint.ref}
          type="button"
          aria-disabled="true"
          data-testid="hero-mint"
          onClick={(e) => e.preventDefault()}
          className="prophets-mint-btn p-nav inline-flex items-center justify-center min-w-[172px] px-8 py-3.5 rounded-sm text-[11px] tracking-[0.4em] uppercase text-white"
          style={disabledStyle}
        >
          Mint
        </button>
      )}
      {marketUsable ? (
        <a
          ref={market.ref}
          onMouseMove={market.onMouseMove}
          onMouseLeave={market.onMouseLeave}
          href={resolvedMarket}
          target="_blank"
          rel="noreferrer"
          data-testid="hero-marketplace"
          data-cursor="hover"
          className="prophets-secondary-btn p-nav inline-flex items-center justify-center min-w-[220px] px-8 py-3.5 rounded-sm text-[11px] tracking-[0.4em] uppercase text-white/90"
        >
          Buy on Marketplace
        </a>
      ) : (
        <button
          ref={market.ref}
          type="button"
          aria-disabled="true"
          data-testid="hero-marketplace"
          onClick={(e) => e.preventDefault()}
          className="prophets-secondary-btn p-nav inline-flex items-center justify-center min-w-[220px] px-8 py-3.5 rounded-sm text-[11px] tracking-[0.4em] uppercase text-white/90"
          style={disabledStyle}
        >
          Buy on Marketplace
        </button>
      )}
    </div>
  );
}
